import { zodResolver } from '@hookform/resolvers/zod';
import type { Paginated, Vehicle } from '@logistech/ui-shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Switch } from '@/components/geofences/GeofenceRulesPanel';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import {
  GEOFENCE_RULE_ERROR_MESSAGES, hasThreshold, MAX_VEHICLES_PER_RULE, RULE_TYPE_DESCRIPTIONS, RULE_TYPE_LABELS, RULE_TYPES,
  THRESHOLD_BOUNDS, useCreateGeofenceRule, useDeleteGeofenceRule, useUpdateGeofenceRule,
  type GeofenceRule, type GeofenceRuleType,
} from '@/lib/geofence-rules';
import { apiErrorCode } from '@/lib/geofences';


const THRESHOLD_LABELS = { SPEED_IN_ZONE: 'Vitesse maximale (km/h)', DWELL: 'Durée maximale (minutes)' } as const;

// Miroir du contrôle rules-engine (thresholdIssue) : seuil requis et borné pour SPEED_IN_ZONE / DWELL seulement
const schema = z
  .object({
    ruleType: z.enum(RULE_TYPES),
    threshold: z.string().trim(),
    allVehicles: z.boolean(),
    vehicleIds: z.array(z.string()).max(MAX_VEHICLES_PER_RULE, `${MAX_VEHICLES_PER_RULE} véhicules maximum`),
    active: z.boolean(),
  })
  .superRefine((d, ctx) => {
    if (hasThreshold(d.ruleType)) {
      const { min, max, unit } = THRESHOLD_BOUNDS[d.ruleType];
      const n = Number(d.threshold);
      if (d.threshold === '') ctx.addIssue({ code: 'custom', path: ['threshold'], message: 'Seuil requis' });
      else if (!/^\d+$/.test(d.threshold) || n < min || n > max) {
        ctx.addIssue({ code: 'custom', path: ['threshold'], message: `Nombre entier entre ${min} et ${max.toLocaleString('fr-FR')} ${unit}` });
      }
    }
    if (!d.allVehicles && d.vehicleIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['vehicleIds'], message: 'Choisissez au moins un véhicule, ou « Tous les véhicules »' });
    }
  });

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = { ruleType: 'ENTER', threshold: '', allVehicles: true, vehicleIds: [], active: true };

const toValues = (r: GeofenceRule): FormValues => ({
  ruleType: r.ruleType,
  threshold: r.threshold == null ? '' : String(r.threshold),
  allVehicles: r.vehicleIds.length === 0,
  vehicleIds: r.vehicleIds,
  active: r.active,
});

interface Props {
  /** Zone de la règle ; null = modal fermée. */
  geofence: { id: string; name: string } | null;
  /** Règle éditée ; null = création. */
  rule: GeofenceRule | null;
  onClose: () => void;
}

export function GeofenceRuleDialog({ geofence, rule, onClose }: Props) {
  const open = geofence != null;
  const geofenceId = geofence?.id ?? '';
  const create = useCreateGeofenceRule();
  const update = useUpdateGeofenceRule(geofenceId);
  const remove = useDeleteGeofenceRule(geofenceId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [search, setSearch] = useState('');

  const { register, handleSubmit, reset, setError, setValue, watch, formState: { errors } } =
    useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    reset(rule ? toValues(rule) : DEFAULTS);
    setSearch('');
    setConfirmDelete(false);
  }, [open, rule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Même requête que les autres sélecteurs de véhicules (PlanDialog, historique des alertes)
  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: async () => (await api.get<Paginated<Vehicle>>('/vehicles?pageSize=100')).data,
    enabled: open,
  });
  const vehicles = vehiclesQuery.data?.data ?? [];

  const ruleType = watch('ruleType');
  const allVehicles = watch('allVehicles');
  const vehicleIds = watch('vehicleIds');
  const active = watch('active');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) => [v.plate, v.make, v.model].some((s) => s?.toLowerCase().includes(q)));
  }, [vehicles, search]);

  const toggleVehicle = (id: string) =>
    setValue('vehicleIds', vehicleIds.includes(id) ? vehicleIds.filter((v) => v !== id) : [...vehicleIds, id], { shouldValidate: true });

  const pending = create.isPending || update.isPending;

  const handleApiError = (err: unknown, fallback: string) => {
    const code = apiErrorCode(err);
    if (code === 'VALIDATION_ERROR') {
      const details = (err as { response?: { data?: { error?: { details?: Array<{ path: string; message: string }> } } } })
        .response?.data?.error?.details ?? [];
      const onThreshold = details.find((d) => d.path === 'threshold');
      if (onThreshold) {
        setError('threshold', { message: onThreshold.message });
        return;
      }
    }
    toast.error((code && GEOFENCE_RULE_ERROR_MESSAGES[code]) ?? apiErrorMessage(err, fallback));
    if (code === 'NOT_FOUND') onClose();
  };

  const submit = (d: FormValues) => {
    const threshold = hasThreshold(d.ruleType) ? Number(d.threshold) : null;
    const ids = d.allVehicles ? [] : d.vehicleIds;
    if (rule) {
      update.mutate(
        { id: rule.id, ...(hasThreshold(d.ruleType) ? { threshold } : {}), vehicleIds: ids, active: d.active },
        {
          onSuccess: () => {
            toast.success('Règle mise à jour');
            onClose();
          },
          onError: (err) => handleApiError(err, 'Modification impossible'),
        },
      );
    } else {
      create.mutate(
        { geofenceId, ruleType: d.ruleType, threshold, vehicleIds: ids, active: d.active },
        {
          onSuccess: () => {
            toast.success('Règle créée');
            onClose();
          },
          onError: (err) => handleApiError(err, 'Création impossible'),
        },
      );
    }
  };

  const doDelete = () => {
    if (!rule) return;
    remove.mutate(rule.id, {
      onSuccess: () => toast.success('Règle supprimée'),
      onError: (err) => handleApiError(err, 'Suppression impossible'),
      onSettled: () => {
        setConfirmDelete(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Dialog open={open && !confirmDelete} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{rule ? "Modifier la règle d'alerte" : "Nouvelle règle d'alerte"}</DialogTitle>
            <DialogDescription>Zone « {geofence?.name} »</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(submit)} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="rule-type">Déclencheur</Label>
              <Select
                value={ruleType}
                disabled={rule != null}
                onValueChange={(v) => {
                  setValue('ruleType', v as GeofenceRuleType);
                  setValue('threshold', '', { shouldValidate: false });
                }}
              >
                <SelectTrigger id="rule-type" aria-label="Déclencheur"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map((t) => <SelectItem key={t} value={t}>{RULE_TYPE_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {RULE_TYPE_DESCRIPTIONS[ruleType]}
                {rule && ". Le déclencheur ne se modifie pas : supprimez la règle puis recréez-la."}
              </p>
            </div>

            {hasThreshold(ruleType) && (
              <div>
                <Label htmlFor="rule-threshold">{THRESHOLD_LABELS[ruleType]} *</Label>
                <Input
                  id="rule-threshold"
                  inputMode="numeric"
                  {...register('threshold')}
                  placeholder={ruleType === 'SPEED_IN_ZONE' ? '60' : '30'}
                  aria-invalid={!!errors.threshold}
                />
                {errors.threshold ? (
                  <p role="alert" className="text-xs text-danger mt-1">{errors.threshold.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    Entre {THRESHOLD_BOUNDS[ruleType].min} et {THRESHOLD_BOUNDS[ruleType].max.toLocaleString('fr-FR')} {THRESHOLD_BOUNDS[ruleType].unit}
                    {ruleType === 'DWELL' && ' (30 jours)'}
                  </p>
                )}
              </div>
            )}

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Véhicules concernés</legend>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('allVehicles')} />
                Tous les véhicules (y compris ceux ajoutés plus tard)
              </label>
              {!allVehicles && (
                <div className="space-y-2">
                  <Input
                    type="search"
                    aria-label="Rechercher un véhicule"
                    placeholder="Rechercher une immatriculation…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <ul aria-label="Véhicules" className="max-h-48 overflow-y-auto rounded-md border divide-y">
                    {vehiclesQuery.isLoading && <li className="p-2 text-xs text-muted-foreground">Chargement…</li>}
                    {vehiclesQuery.isError && <li className="p-2 text-xs text-danger">Liste des véhicules indisponible</li>}
                    {vehiclesQuery.isSuccess && filtered.length === 0 && (
                      <li className="p-2 text-xs text-muted-foreground">Aucun véhicule</li>
                    )}
                    {filtered.map((v) => (
                      <li key={v.id}>
                        <label className="flex items-center gap-2 p-2 text-sm hover:bg-muted cursor-pointer">
                          <input type="checkbox" checked={vehicleIds.includes(v.id)} onChange={() => toggleVehicle(v.id)} />
                          <span className="font-medium">{v.plate}</span>
                          <span className="text-xs text-muted-foreground truncate">{[v.make, v.model].filter(Boolean).join(' ')}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    {vehicleIds.length} véhicule{vehicleIds.length > 1 ? 's' : ''} sélectionné{vehicleIds.length > 1 ? 's' : ''}
                  </p>
                </div>
              )}
              {errors.vehicleIds && <p role="alert" className="text-xs text-danger">{errors.vehicleIds.message}</p>}
            </fieldset>

            <div className="flex items-center gap-3">
              <Switch
                id="rule-active"
                checked={active}
                label="Règle active"
                onChange={() => setValue('active', !active)}
              />
              <Label htmlFor="rule-active" className="font-normal">
                {active ? 'Active : la règle est évaluée' : "Inactive : la règle n'est pas évaluée"}
              </Label>
            </div>

            <DialogFooter className="gap-2 sm:justify-between">
              {rule ? (
                <Button type="button" variant="outline" className="text-danger" onClick={() => setConfirmDelete(true)}>Supprimer</Button>
              ) : <span />}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" type="button" onClick={onClose}>Annuler</Button>
                <Button type="submit" disabled={pending}>{pending ? 'Enregistrement…' : rule ? 'Enregistrer' : 'Créer la règle'}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open && confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer la règle</DialogTitle>
            <DialogDescription>
              La règle « {rule ? RULE_TYPE_LABELS[rule.ruleType] : ''} » de la zone « {geofence?.name} » sera supprimée. Les
              alertes déjà émises sont conservées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Annuler</Button>
            <Button variant="destructive" disabled={remove.isPending} onClick={doDelete}>
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
